import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Badge, Box, Flex, ErrorMessage, Spinner, Text, useToast } from '_shared/designSystem/components';
import { useSetRecoilState } from 'recoil';
import { mobileHeaderTextState } from '_shared/globalState/atoms';
import { useSearch, useMatch, useNavigate } from '@tanstack/react-location';
import StickyHeader from '_shared/components/StickyHeader';
import isEmpty from 'lodash/isEmpty';
import { MatchInfoHeader } from 'match/components/MatchInfoHeader';
import { useGetReviewQuery } from 'review/hooks/reviewHooks';
import ComparisonScores from './ComparisonScores';
import CollectionForm from 'collect/components/CollectionForm';
import { initialCollectionFormData } from '_shared/constants/collectionTypes';
import { applyReviewFieldToFormData } from 'review/util/reviewFormStateUtil';
import { reviewService } from 'review/services/reviewService';
import { playerService } from '_shared/services/playerService';
import { useMutation } from 'react-query';
import { userPermissionTypes } from '_shared/constants/user';
import { userPermissionsState } from '_shared/globalState/atoms';
import { checkPermission } from '_shared/utils/permissions';
import { useRecoilValue } from 'recoil';
import NoPermissionAlert from '_shared/components/NoPermissionAlert';
import CountdownBadge from '_shared/components/CountdownBadge';

export default function Review() {
  const {
    params: { pointJoinKey }
  } = useMatch();

  const [errorMessage, setErrorMessage] = useState(null);

  const { matchId } = useSearch();
  const navigate = useNavigate();

  const setMobileHeaderText = useSetRecoilState(mobileHeaderTextState);
  const [formData, setFormData] = useState(initialCollectionFormData);
  const toast = useToast();

  const { isLoading, error, data } = useGetReviewQuery(matchId, pointJoinKey);

  const players = `${data?.match_info?.player1Name} v ${data?.match_info?.player2Name}`;

  const { mutate: postReviewMutate, isLoading: isPostReviewLoading } = useMutation(reviewService.postReview, {
    onSuccess: (data) => {
      navigate({ to: `/flagged` });
    },
    onError: (error) => {
      if (error?.response?.status === 403) {
        setErrorMessage('You do not have permission to perform this action');
      } else {
        setErrorMessage('An error occured when submitting the review. Please try again');
      }
    }
  });

  const { mutate: postCannotReviewMutate } = useMutation(reviewService.postCannotReview, {
    onSuccess: (data) => {
      navigate({ to: `/flagged` });
    },
    onError: (error) => {
      if (error?.response?.status === 403) {
        setErrorMessage('You do not have permission to perform this action');
      } else {
        setErrorMessage('An error occured when submitting the review. Please try again');
      }
    }
  });

  const { mutate: updateHandednessMutate } = useMutation(playerService.updatePlayerHandedness, {
    onSuccess: () => {
      toast({
        title: 'Handedness updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      // Refetch the data to update the UI
      window.location.reload();
    },
    onError: (error) => {
      toast({
        title: 'Failed to update handedness',
        description: error?.response?.data?.message || 'An error occurred while updating handedness',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  useEffect(() => {
    if (!isEmpty(players)) {
      setMobileHeaderText(`Review - ${players}`);
    }
  }, [setMobileHeaderText, players]);

  useEffect(() => {
    if (data?.point_data?.manual) {
      setFormData(data?.point_data?.manual);
    }
  }, [setFormData, data?.point_data?.manual]);

  const { permissions } = useRecoilValue(userPermissionsState);

  if (!checkPermission(userPermissionTypes.REVIEW, permissions)) {
    return <NoPermissionAlert />;
  }

  const updateFormData = (field, value) => {
    flushSync(() => {
      setFormData((prev) => applyReviewFieldToFormData(prev, field, value));
    });
  };

  const handleHandednessUpdate = (playerId, handedness) => {
    updateHandednessMutate({ matchId, playerId, handedness });
  };

  const handleNextOrSubmit = async () => {
    const dataToSubmit = {
      matchId,
      pointJoinKey,
      set: data?.set,
      game: data?.game,
      point: data?.point,
      formData
    };
    postReviewMutate(dataToSubmit);
  };

  const handleCannotReviewItem = async () => {
    const dataToSubmit = {
      matchId,
      pointJoinKey,
      set: data?.set,
      game: data?.game,
      point: data?.point
    };
    postCannotReviewMutate(dataToSubmit);
  };

  if (error)
    return (
      <Box>
        <StickyHeader text="Review" />
        <ErrorMessage message="Unable to retrieve review info, please try refreshing the page." />
      </Box>
    );

  if (isLoading)
    return (
      <Box>
        <Flex pt={100} justify="center">
          <Spinner color="primary.500" />
        </Flex>
      </Box>
    );

  return (
    <Flex justify="center" position={'relative'}>
      <Box position="absolute" top="10px" right="10px">
        <CountdownBadge flaggedTime={sessionStorage.getItem('flaggedTime')} />
      </Box>
      <Flex px={5} mt={2} gap={8} direction="column" maxW="2200px" pb={10} align="center">
        <MatchInfoHeader
          subheading="Review"
          matchInfo={data?.match_info}
          matchId={matchId}
          pointJoinKey={pointJoinKey}
          onHandednessUpdate={handleHandednessUpdate}
        />
        {errorMessage && <ErrorMessage message={errorMessage} />}
        <Flex justify={{ base: 'center', md: 'initial' }}>
          <ScoreInfo
            score={data?.flagged_point_score}
            pointJoinKey={pointJoinKey}
            set={data?.set}
            game={data?.game}
            point={data?.point}
            isAce={data?.is_ace}
            isDoubleFault={data?.is_double_fault}
          />
        </Flex>
        <Flex flexWrap="wrap" gap={5} mb={{ base: 5, md: 0 }}>
          <Flex justify="center" align="center">
            <ComparisonScores
              data={data?.point_data}
              player1Name={data?.match_info?.player1Name}
              player2Name={data?.match_info?.player2Name}
            />
          </Flex>
        </Flex>
        <CollectionForm
          formData={formData}
          updateFormData={updateFormData}
          player1Name={data?.match_info?.player1Name}
          player2Name={data?.match_info?.player2Name}
          submitCollectionItem={null}
          handleNextOrSubmit={handleNextOrSubmit}
          handleCannotReviewItem={handleCannotReviewItem}
          reviewing={true}
          showNextPointLoading={isPostReviewLoading}
        />
      </Flex>
    </Flex>
  );
}

const ScoreInfo = ({ score, pointJoinKey, set, game, point, isAce, isDoubleFault }) => (
  <Flex direction="column" gap={3}>
    {(isAce || isDoubleFault) && (
      <Flex gap={2} justify="center" flexWrap="wrap" aria-label="Point serve outcome">
        {isAce && <Badge text="Ace" colour="success" fontSize="sm" px={2.5} py={0.5} />}
        {isDoubleFault && <Badge text="DF" colour="warning" fontSize="sm" px={2.5} py={0.5} />}
      </Flex>
    )}
    <Flex gap={4} justify="center">
      <Text>Score at point:</Text>
      <Flex gap={3}>
        <Text>{`${nth(score?.set_number)} Set`}</Text>
        <Text>{score?.game_score}</Text>
        <Text>{score?.point_score}</Text>
      </Flex>
    </Flex>
    <Text>{`Set ${set} - Game ${game} - Point ${point} - PJK ${pointJoinKey}`}</Text>
  </Flex>
);

export function nth(n) {
  return `${n}${['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th'}`;
}
